import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  AccountBalance,
  LocalGasStation,
  Room,
  AccountBalanceWallet,
  SupportAgent,
  Visibility,
  NotificationsNone,
  CreditCard,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  GasMeter,
  CalendarToday,
  LocationOn
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { quikqAuthService } from '../services/quikqAuth';
import { quikqApiService } from '../services/quikqApi';
import { SavingsSummary, SavingsTransaction, CardInfo } from '../services/quikqApi';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingsData, setSavingsData] = useState<SavingsSummary | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<SavingsTransaction[]>([]);
  const [cards, setCards] = useState<CardInfo[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [yearlySavings, setYearlySavings] = useState(0);
  const [quarterlySavings, setQuarterlySavings] = useState(0);
  const [monthlySavings, setMonthlySavings] = useState(0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate period savings
  const calculatePeriodSavings = async () => {
    try {
      const currentUser = quikqAuthService.getCurrentUser();
      if (!currentUser?.startCode) {
        return;
      }

      const now = new Date();
      
      // Yearly (last 365 days)
      const yearlyStart = new Date();
      yearlyStart.setDate(yearlyStart.getDate() - 365);
      
      // Quarterly (last 90 days)
      const quarterlyStart = new Date();
      quarterlyStart.setDate(quarterlyStart.getDate() - 90);
      
      // Monthly (last 30 days)
      const monthlyStart = new Date();
      monthlyStart.setDate(monthlyStart.getDate() - 30);

      // Fetch data for each period
      const [yearlyData, quarterlyData, monthlyData] = await Promise.all([
        fetchPeriodData(yearlyStart, now),
        fetchPeriodData(quarterlyStart, now),
        fetchPeriodData(monthlyStart, now)
      ]);

      setYearlySavings(yearlyData);
      setQuarterlySavings(quarterlyData);
      setMonthlySavings(monthlyData);
    } catch (error) {
      console.error('Error calculating period savings:', error);
    }
  };

  const fetchPeriodData = async (startDate: Date, endDate: Date): Promise<number> => {
    try {
      let transactionsResponse;
      try {
        transactionsResponse = await quikqApiService.getQuikQTransactionsRickFormatWithStartCode(
          startDate.toISOString(),
          endDate.toISOString()
        );
      } catch (startCodeError) {
        try {
          transactionsResponse = await quikqApiService.getQuikQCarrierPartnerTransactions(
            startDate.toISOString(),
            endDate.toISOString()
          );
        } catch (carrierError) {
          transactionsResponse = await quikqApiService.getQuikQTransactionsRickFormat(
            startDate.toISOString(),
            endDate.toISOString()
          );
        }
      }

      if (transactionsResponse?.data?.report?.transactions) {
        const summary = calculateSavingsFromQuikQTransactions(
          transactionsResponse.data.report.transactions, 
          startDate, 
          endDate
        );
        return summary.totalSaved;
      }
      return 0;
    } catch (error) {
      console.error('Error fetching period data:', error);
      return 0;
    }
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const currentUser = quikqAuthService.getCurrentUser();
      if (!currentUser?.startCode) {
        throw new Error('No authenticated user found');
      }

      // Fetch user profile
      setUserProfile(currentUser);

      // Fetch cards
      try {
        const cardsData = await quikqApiService.getCarrierCards(currentUser.startCode);
        setCards(cardsData);
      } catch (err) {
        // console.log('⚠️ Could not fetch cards:', err);
        setCards([]);
      }

      // Fetch QuikQ transactions and calculate savings for last 2 years (730 days)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 730);

      try {
        let transactionsResponse;
        try {
          // console.log('🔍 Using startCode method directly...');
          transactionsResponse = await quikqApiService.getQuikQTransactionsRickFormatWithStartCode(
            startDate.toISOString(),
            endDate.toISOString()
          );
        } catch (startCodeError) {
          // console.log('🔍 StartCode method failed, trying carrierPartners...', startCodeError);
          try {
            transactionsResponse = await quikqApiService.getQuikQCarrierPartnerTransactions(
              startDate.toISOString(),
              endDate.toISOString()
            );
          } catch (carrierError) {
            // console.log('🔍 Carrier method failed, trying general endpoint...', carrierError);
            transactionsResponse = await quikqApiService.getQuikQTransactionsRickFormat(
              startDate.toISOString(),
              endDate.toISOString()
            );
          }
        }

        if (transactionsResponse?.data?.report?.transactions) {
          // Transform QuikQ transactions to savings format
          const savingsTransactions = transformQuikQToSavingsTransactions(transactionsResponse.data.report.transactions, startDate, endDate);
          setRecentTransactions(savingsTransactions.slice(0, 5)); // Last 5 transactions
          
          // Calculate savings summary
          const summary = calculateSavingsFromQuikQTransactions(transactionsResponse.data.report.transactions, startDate, endDate);
          setSavingsData(summary);
        } else {
          setRecentTransactions([]);
          setSavingsData(null);
        }
      } catch (err) {
        // console.log('⚠️ Could not fetch transaction data:', err);
        setRecentTransactions([]);
        setSavingsData(null);
      }

      // Calculate period savings
      await calculatePeriodSavings();

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Transform QuikQ transactions to SavingsTransaction format with date filtering
  const transformQuikQToSavingsTransactions = (quikqTransactions: any[], startDate: Date, endDate: Date): SavingsTransaction[] => {
    try {
      const savingsTransactions: SavingsTransaction[] = [];
      
      quikqTransactions.forEach((transactionWrapper, index) => {
        // Handle both direct transaction objects and wrapped transaction objects
        const transaction = 'transaction' in transactionWrapper ? transactionWrapper.transaction : transactionWrapper;
        
        if (!transaction.subTransactions || !Array.isArray(transaction.subTransactions)) {
          console.warn('Invalid transaction structure:', transactionWrapper);
          return;
        }

        // Filter by date - only include transactions within the selected date range
        // Parse QuikQ date format: "2025-04-15-23:54:42" -> "2025-04-15T23:54:42"
        const rawDate = transaction.beginDate || new Date().toISOString();
        const formattedDate = rawDate.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2}:\d{2})/, '$1T$2');
        const transactionDate = new Date(formattedDate);
        const isWithinDateRange = transactionDate >= startDate && transactionDate <= endDate;
        
        if (!isWithinDateRange) {
          return; // Skip transactions outside date range
        }

        transaction.subTransactions.forEach((subTransWrapper: any, subIndex: number) => {
          try {
            // Handle both direct subTransaction objects and wrapped subTransaction objects
            const subTrans = 'subTransaction' in subTransWrapper ? subTransWrapper.subTransaction : subTransWrapper;
            
            const retailTotal = subTrans.subTotal || 0;
            const adjustedTotal = subTrans.adjSubTotal || 0;
            const savings = retailTotal - adjustedTotal;
            
            if (savings > 0) { // Only include transactions with savings
              // Convert QuikQ date format to ISO format for proper display
              const formattedDate = rawDate.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2}:\d{2})/, '$1T$2');
              
              savingsTransactions.push({
                transactionId: `${transaction.seq}-${subTrans.subTransNum}`,
                cardNo: transaction.magCard || 'Unknown',
                stationCode: transaction.station || 'Unknown',
                stationName: `Station ${transaction.station || 'Unknown'}`,
                transactionDate: formattedDate, // Use properly formatted date
                fuelType: 'Fuel', // Default since QuikQ doesn't specify fuel type
                gallons: 0, // QuikQ doesn't provide gallons
                retailPrice: retailTotal,
                adjustedPrice: adjustedTotal,
                savings: savings,
                totalAmount: adjustedTotal,
                location: {
                  address: 'N/A',
                  city: 'N/A',
                  state: 'N/A',
                  zip: 'N/A'
                }
              });
            }
          } catch (subTransError) {
            console.warn('Error processing sub-transaction:', subTransError, subTransWrapper);
          }
        });
      });
      
      return savingsTransactions;
    } catch (error) {
      console.error('Error transforming QuikQ transactions:', error);
      return [];
    }
  };

  // Calculate savings summary from QuikQ transactions with date filtering
  const calculateSavingsFromQuikQTransactions = (transactions: any[], startDate: Date, endDate: Date): SavingsSummary => {
    try {
      let totalSaved = 0;
      let totalSpent = 0;
      let filteredTransactionCount = 0;
      let transactionsWithSavings = 0;
      const stationMap = new Map<string, { saved: number; spent: number; count: number }>();
      const cardMap = new Map<string, { saved: number; spent: number; count: number }>();

      transactions.forEach((transactionWrapper) => {
        // Handle both direct transaction objects and wrapped transaction objects
        const transaction = 'transaction' in transactionWrapper ? transactionWrapper.transaction : transactionWrapper;
        
        if (!transaction.subTransactions || !Array.isArray(transaction.subTransactions)) {
          return;
        }

        // Filter by date - only include transactions within the selected date range
        // Parse QuikQ date format: "2025-04-15-23:54:42" -> "2025-04-15T23:54:42"
        const rawDate = transaction.beginDate || new Date().toISOString();
        const formattedDate = rawDate.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2}:\d{2})/, '$1T$2');
        const transactionDate = new Date(formattedDate);
        const isWithinDateRange = transactionDate >= startDate && transactionDate <= endDate;
        
        if (!isWithinDateRange) {
          return; // Skip transactions outside date range
        }

        filteredTransactionCount++; // Count filtered transactions

        transaction.subTransactions.forEach((subTransWrapper: any) => {
          try {
            // Handle both direct subTransaction objects and wrapped subTransaction objects
            const subTrans = 'subTransaction' in subTransWrapper ? subTransWrapper.subTransaction : subTransWrapper;
            
            const retailTotal = subTrans.subTotal || 0;
            const adjustedTotal = subTrans.adjSubTotal || 0;
            const savings = retailTotal - adjustedTotal;
            
            totalSaved += savings;
            totalSpent += adjustedTotal;

            // Only count transactions with savings for Fuel Stops
            if (savings > 0) {
              transactionsWithSavings++;
            }

            // Track by station - ALL transactions in date range
            if (transaction.station) {
              const stationKey = `${transaction.station}-${transaction.seq}-${subTrans.subTransNum}`;
              const station = stationMap.get(stationKey) || { saved: 0, spent: 0, count: 0 };
              station.saved += savings;
              station.spent += adjustedTotal;
              station.count += 1;
              stationMap.set(stationKey, station);
            }

            // Track by card - ALL transactions in date range
            if (transaction.magCard) {
              const cardKey = `${transaction.magCard}-${transaction.seq}-${subTrans.subTransNum}`;
              const card = cardMap.get(cardKey) || { saved: 0, spent: 0, count: 0 };
              card.saved += savings;
              card.spent += adjustedTotal;
              card.count += 1;
              cardMap.set(cardKey, card);
            }
          } catch (subTransError) {
            console.warn('Error processing sub-transaction in summary calculation:', subTransError, subTransWrapper);
          }
        });
      });

      // Convert maps to arrays for display - each transaction separately
      const savingsByStation = Array.from(stationMap.entries())
        .map(([stationKey, data]) => {
          const [stationCode, seq, subTransNum] = stationKey.split('-');
          return {
            stationCode,
            stationName: `Station ${stationCode}`,
            totalSaved: data.saved,
            totalSpent: data.spent,
            transactionCount: data.count,
            averageSavings: data.count > 0 ? data.saved / data.count : 0,
          };
        })
        .sort((a, b) => b.totalSaved - a.totalSaved);

      const savingsByCard = Array.from(cardMap.entries())
        .map(([cardKey, data]) => {
          const [cardNo, seq, subTransNum] = cardKey.split('-');
          return {
            cardNo,
            cardType: 'Driver', // Default type
            totalSaved: data.saved,
            totalSpent: data.spent,
            transactionCount: data.count,
            averageSavings: data.count > 0 ? data.saved / data.count : 0,
          };
        })
        .sort((a, b) => b.totalSaved - a.totalSaved);

      return {
        totalSaved,
        totalSpent,
        totalTransactions: filteredTransactionCount, // Use count of ALL transactions in date range
        averageSavings: transactionsWithSavings > 0 ? totalSaved / transactionsWithSavings : 0,
        savingsByStation,
        savingsByCard,
      };
    } catch (error) {
      console.error('Error calculating savings summary:', error);
      return {
        totalSaved: 0,
        totalSpent: 0,
        totalTransactions: 0,
        averageSavings: 0,
        savingsByStation: [],
        savingsByCard: [],
      };
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'find-fuel':
        navigate('/map');
        break;
      case 'view-savings':
        navigate('/savings');
        break;
      case 'wallet':
        navigate('/cards');
        break;
      case 'support':
        navigate('/support');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, pb: { xs: 12, sm: 12, md: 12 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 2, 
      pb: { xs: 12, sm: 12, md: 12 },
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '300px',
        height: '300px',
        backgroundImage: 'url(/NSFIcon.png)',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        opacity: 0.03,
        zIndex: -1,
        pointerEvents: 'none'
      }
    }}>
      {/* Header with Split Watermark - Left: NSFLogo, Right: Empty (for future custom logo) */}
      <Box sx={{ 
        position: 'relative', 
        mb: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          left: -50,
          width: '100%',
          height: '200px',
          background: `url('/NSFIcon.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none'
        }
      }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ 
          fontSize: { xs: 24, sm: 28, md: 32 },
          mb: { xs: 1, sm: 2 },
          position: 'relative',
          zIndex: 1
        }}>
          Welcome to Dashboard
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Quick Actions */}
      <Paper elevation={1} sx={{ 
        p: { xs: 2, sm: 3, md: 4 }, 
        mb: { xs: 2, sm: 3, md: 4 } 
      }}>
        <Typography fontWeight={700} mb={2} color="#4f4f4f" fontSize={{ xs: 16, sm: 18, md: 20 }}>
          Quick Actions
        </Typography>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: { xs: 2, sm: 3, md: 4 }
        }}>
          <Button 
            variant="outlined" 
            sx={{ 
              flexDirection: 'column', 
              py: { xs: 2, sm: 2.5, md: 3 }, 
              borderColor: '#1A5FB4', 
              color: '#1A5FB4',
              minHeight: { xs: '80px', sm: '90px', md: '100px' }
            }}
            onClick={() => handleQuickAction('find-fuel')}
          >
            <Room sx={{ 
              fontSize: { xs: 24, sm: 28, md: 32 }, 
              mb: 0.5 
            }} />
            <Typography fontSize={{ xs: 12, sm: 13, md: 14 }}>Find Fuel</Typography>
          </Button>
          <Button 
            variant="outlined" 
            sx={{ 
              flexDirection: 'column', 
              py: { xs: 2, sm: 2.5, md: 3 }, 
              borderColor: '#81c784', 
              color: '#388e3c',
              minHeight: { xs: '80px', sm: '90px', md: '100px' }
            }}
            onClick={() => handleQuickAction('view-savings')}
          >
            <Visibility sx={{ 
              fontSize: { xs: 24, sm: 28, md: 32 }, 
              mb: 0.5 
            }} />
            <Typography fontSize={{ xs: 12, sm: 13, md: 14 }}>View Savings</Typography>
          </Button>
          <Button 
            variant="outlined" 
            sx={{ 
              flexDirection: 'column', 
              py: { xs: 2, sm: 2.5, md: 3 }, 
              borderColor: '#ba68c8', 
              color: '#6a1b9a',
              minHeight: { xs: '80px', sm: '90px', md: '100px' }
            }}
            onClick={() => handleQuickAction('wallet')}
          >
            <AccountBalanceWallet sx={{ 
              fontSize: { xs: 24, sm: 28, md: 32 }, 
              mb: 0.5 
            }} />
            <Typography fontSize={{ xs: 12, sm: 13, md: 14 }}>Wallet</Typography>
          </Button>
          <Button 
            variant="outlined" 
            sx={{ 
              flexDirection: 'column', 
              py: { xs: 2, sm: 2.5, md: 3 }, 
              borderColor: '#bdbdbd', 
              color: '#4f4f4f',
              minHeight: { xs: '80px', sm: '90px', md: '100px' }
            }}
            onClick={() => handleQuickAction('support')}
          >
            <SupportAgent sx={{ 
              fontSize: { xs: 24, sm: 28, md: 32 }, 
              mb: 0.5 
            }} />
            <Typography fontSize={{ xs: 12, sm: 13, md: 14 }}>Support</Typography>
          </Button>
        </Box>
      </Paper>

      {/* Overview Cards */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: { xs: 1, sm: 2, md: 3 }, 
        mb: { xs: 2, sm: 3, md: 4 } 
      }}>
        <Paper elevation={2} sx={{ 
          p: { xs: 1.5, sm: 2.5, md: 3 }, 
          textAlign: 'center' 
        }}>
          <AccountBalance sx={{ 
            color: '#1A5FB4', 
            fontSize: { xs: 20, sm: 32, md: 36 } 
          }} />
          <Typography variant="h6" fontWeight={700} mt={1} fontSize={{ xs: 14, sm: 18, md: 20 }}>
            {savingsData ? formatCurrency(savingsData.totalSaved) : '$0'}
          </Typography>
          <Typography variant="caption" color="#4f4f4f" fontSize={{ xs: 10, sm: 12, md: 13 }}>
            Total Saved (2 Years)
          </Typography>
        </Paper>
        <Paper elevation={2} sx={{ 
          p: { xs: 1.5, sm: 2.5, md: 3 }, 
          textAlign: 'center' 
        }}>
          <LocalGasStation sx={{ 
            color: '#1A5FB4', 
            fontSize: { xs: 20, sm: 32, md: 36 } 
          }} />
          <Typography variant="h6" fontWeight={700} mt={1} fontSize={{ xs: 14, sm: 18, md: 20 }}>
            {savingsData ? savingsData.totalTransactions : 0}
          </Typography>
          <Typography variant="caption" color="#4f4f4f" fontSize={{ xs: 10, sm: 12, md: 13 }}>
            Fuel Stops
          </Typography>
        </Paper>
                 <Paper elevation={2} sx={{ 
           p: { xs: 1.5, sm: 2.5, md: 3 }, 
           textAlign: 'center' 
         }}>
           <CreditCard sx={{ 
             color: '#1A5FB4', 
             fontSize: { xs: 20, sm: 32, md: 36 } 
           }} />
           <Typography variant="h6" fontWeight={700} mt={1} fontSize={{ xs: 14, sm: 18, md: 20 }}>
             {cards.filter(card => card.status === 'Active').length}
           </Typography>
           <Typography variant="caption" color="#4f4f4f" fontSize={{ xs: 10, sm: 12, md: 13 }}>
             Active Cards
           </Typography>
         </Paper>
      </Box>



      {/* Saved Money */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography fontWeight={700} mb={2} color="#4f4f4f">
          Saved Money
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
          {/* Yearly */}
          <Card elevation={0} sx={{ bgcolor: '#f8f9fa', p: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {formatCurrency(yearlySavings)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Yearly
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last 365 days
              </Typography>
            </Box>
          </Card>

          {/* Quarterly */}
          <Card elevation={0} sx={{ bgcolor: '#f8f9fa', p: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {formatCurrency(quarterlySavings)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Quarterly
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last 90 days
              </Typography>
            </Box>
          </Card>

          {/* Monthly */}
          <Card elevation={0} sx={{ bgcolor: '#f8f9fa', p: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {formatCurrency(monthlySavings)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Monthly
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last 30 days
              </Typography>
            </Box>
          </Card>
        </Box>
      </Paper>

             {/* Active Cards */}
       {cards.filter(card => card.status === 'Active').length > 0 && (
         <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
           <Typography fontWeight={700} mb={2} color="#4f4f4f">
             Active Cards
           </Typography>
           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
             {cards
               .filter(card => card.status === 'Active')
               .slice(0, 3)
               .map((card) => (
               <Card key={card.cardNo} elevation={0} sx={{ bgcolor: '#f8f9fa' }}>
                 <CardContent sx={{ py: 1.5, px: 2 }}>
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                       <CreditCard sx={{ fontSize: 20, color: 'primary.main' }} />
                       <Box>
                         <Typography variant="body2" fontWeight={600}>
                           Card {card.cardNo.slice(-4)}
                         </Typography>
                         <Typography variant="caption" color="text.secondary">
                           {card.cardType}
                         </Typography>
                       </Box>
                     </Box>
                     <Chip 
                       label={card.status}
                       size="small"
                       color="success"
                     />
                   </Box>
                 </CardContent>
               </Card>
             ))}
           </Box>
         </Paper>
       )}

      
    </Box>
  );
};

export default Dashboard; 